import React from 'react';
import { Box, Collapse, Paper, Tooltip } from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import InfoChip, { DAWInfoChip } from '../../InfoChip';
import { formatFileSize } from '../../../../utils/fileUtils';
import { SelectedProjectSummary } from '../../../../models/batchProjectUpload';

interface ProjectSummaryCardProps {
  summary: SelectedProjectSummary;
}

// Base chip style for stat chips
const baseStatChipSx = {
  height: 28,
  borderWidth: 1.5,
  borderRadius: 1.5,
  '& .MuiChip-label': {
    fontSize: '0.875rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  },
};

// DAW chip styles
const dawChipSx = {
  height: 28,
  borderWidth: 1.5,
  borderRadius: 1.5,
  '& .MuiChip-label': {
    fontSize: '0.875rem',
    fontWeight: 600,
    px: 1,
  },
  '& .MuiChip-icon': {
    marginLeft: '10px',
    marginRight: '-4px',
  },
};

const ProjectSummaryCard: React.FC<ProjectSummaryCardProps> = ({ summary }) => {
  const {
    selectedCount,
    totalDetected,
    totalSelectedFiles,
    totalSelectedSize,
    dawDistribution,
    duplicateCount,
  } = summary;

  return (
    <Paper
      variant="inset"
      sx={{
        p: 2,
        mb: 1.5,
        position: 'relative',
      }}
    >
      {/* First line: Title and stats */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-start"
        gap={0.5}
        flexWrap="wrap"
      >
        <InfoChip
          label="Selected"
          value={selectedCount}
          color="primary"
          variant="soft"
          size="small"
          valueColor="primary.main"
          sx={baseStatChipSx}
        />
        <InfoChip
          label="Total"
          value={totalDetected}
          variant="soft"
          size="small"
          sx={baseStatChipSx}
        />
        <InfoChip
          label="Files"
          value={totalSelectedFiles}
          color="success"
          variant="soft"
          size="small"
          valueColor="success.main"
          sx={baseStatChipSx}
        />
        <InfoChip
          label="Size"
          value={formatFileSize(totalSelectedSize)}
          color="info"
          variant="soft"
          size="small"
          valueColor="info.main"
          sx={baseStatChipSx}
        />
        {duplicateCount > 0 && (
          <Tooltip
            title="Projects with names that already exist in your account. You'll need to delete the existing cloud project or rename the new local project."
            arrow
            placement="top"
          >
            <InfoChip
              icon={
                <WarningIcon fontSize="small" sx={{ fontSize: '0.875rem' }} />
              }
              label={`Duplicate${duplicateCount > 1 ? 's' : ''}`}
              value={duplicateCount}
              color="warning"
              variant="soft"
              size="small"
              cautionTape
              valueColor="warning.main"
              sx={baseStatChipSx}
            />
          </Tooltip>
        )}
      </Box>

      {/* Second line: DAW chips */}
      <Collapse
        in={selectedCount > 0}
        timeout={{
          enter: 200,
          exit: 200,
        }}
      >
        <Box
          display="flex"
          gap={1.5}
          alignItems="center"
          flexWrap="wrap"
          pt={selectedCount > 0 ? 1.5 : 0}
        >
          {Object.entries(dawDistribution)?.map(([dawType, count]) => (
            <DAWInfoChip
              key={dawType}
              dawType={dawType}
              count={count}
              size="small"
              variant="soft"
              sx={dawChipSx}
            />
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ProjectSummaryCard;
